import React from 'react';
import { useTranslation } from 'react-i18next';
import { EditMode } from '../../../hooks/useCaptionEditor';
import { WordCountControl } from './WordCountControl';

interface CaptionEditorControlsProps {
  editMode: EditMode;
  setEditMode: (mode: EditMode) => void;
  applyToAllCaptions: boolean;
  setApplyToAllCaptions: (value: boolean) => void;
  excludeCaptions: boolean;
  setExcludeCaptions: (value: boolean) => void;
  excludeTitles: boolean;
  setExcludeTitles: (value: boolean) => void;
  maxWordsPerCaption: number;
  setMaxWordsPerCaption: (value: number) => void;
  hasExistingCaptions: boolean;
  isRefetchingCaptions: boolean;
  disabledBtnPendingDownload: boolean;
  onRefetchCaptions: () => void;
}

export const CaptionEditorControls: React.FC<CaptionEditorControlsProps> = ({
  editMode,
  setEditMode,
  applyToAllCaptions,
  setApplyToAllCaptions,
  excludeCaptions,
  setExcludeCaptions,
  excludeTitles,
  setExcludeTitles,
  maxWordsPerCaption,
  setMaxWordsPerCaption,
  hasExistingCaptions,
  isRefetchingCaptions,
  disabledBtnPendingDownload,
  onRefetchCaptions
}) => {
  const { t } = useTranslation('captionEditor');
  
  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1rem',
        gap: '8px'
      }}>
        {/* <span style={{ fontSize: '20px' }}>🤖</span> */}
        <h3 style={{ 
          color: '#e0e0e0', 
          margin: 0, 
          fontSize: '1.1rem',
          fontWeight: '600'
        }}>
          {t('captionEditor.controls.contentMode.title')}
        </h3>
      </div>
      
      {/* Mode Toggle */}
      <div style={{
        backgroundColor: '#2a2a2a',
        border: '1px solid #444',
        borderRadius: '12px',
        padding: '8px',
        marginBottom: '16px',
        display: 'flex',
        gap: '4px'
      }}>
        <button
          onClick={() => setEditMode('captions')}
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '8px',
            border: 'none',
            background: editMode === 'captions' ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
            color: editMode === 'captions' ? '#ffffff' : '#888',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          {t('captionEditor.controls.contentMode.captionMode')}
        </button>
        <button
          onClick={() => setEditMode('titles')}
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: '8px',
            border: 'none',
            background: editMode === 'titles' ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'transparent',
            color: editMode === 'titles' ? '#ffffff' : '#888',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          {t('captionEditor.controls.contentMode.titleMode')}
        </button>
      </div>

      {/* Exclude options for video generation */}
      <div style={{
        backgroundColor: '#2a2a2a',
        border: '1px solid #444',
        borderRadius: '12px',
        padding: '12px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <input
          type="checkbox"
          checked={editMode === 'captions' ? excludeTitles : excludeCaptions}
          onChange={(e) => {
            if (editMode === 'captions') {
              setExcludeTitles(e.target.checked);
            } else {
              setExcludeCaptions(e.target.checked);
            }
          }}
          style={{
            width: '18px',
            height: '18px',
            accentColor: '#8b5cf6'
          }}
        />
        <span style={{ 
          color: '#e0e0e0', 
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {editMode === 'captions' 
            ? t('captionEditor.controls.excludeOptions.excludeTitlesFromVideo')
            : t('captionEditor.controls.excludeOptions.excludeCaptionsFromVideo')
          }
        </span>
      </div>

      {/* Apply Style to All checkbox */}
      <div style={{
        backgroundColor: '#2a2a2a',
        border: '1px solid #444',
        borderRadius: '12px',
        padding: '12px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <input
          type="checkbox"
          checked={applyToAllCaptions}
          onChange={(e) => setApplyToAllCaptions(e.target.checked)}
          style={{
            width: '18px',
            height: '18px',
            accentColor: '#8b5cf6'
          }}
        />
        <span style={{ 
          color: '#e0e0e0', 
          fontSize: '14px',
          fontWeight: '500'
        }}>
          {t('captionEditor.controls.applyStyle.label')}
        </span>
      </div>

      {/* Words Per Caption Control - Only for captions mode */}
      {editMode === 'captions' && hasExistingCaptions && (
        <WordCountControl
          maxWordsPerCaption={maxWordsPerCaption}
          setMaxWordsPerCaption={setMaxWordsPerCaption}
          isRefetchingCaptions={isRefetchingCaptions}
          disabledBtnPendingDownload={disabledBtnPendingDownload}
          onRefetchCaptions={onRefetchCaptions}
        />
      )}
    </div>
  );
};
